import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createProduct, updateProduct } from '../../../shared/api/products.js'
import type { Category, Product, ProductVariant } from '../../../shared/types/models.js'
import {
  normalizeOptionalNumber,
  normalizePrice,
  normalizeSortOrder,
  validateNameField,
  validateOptionalNumber,
  validatePriceField,
  validateSortOrderField,
} from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'
import VariantsEditor from './VariantsEditor.js'
import ModifierGroupsEditor from './ModifierGroupsEditor.js'
import AvailabilitySection from './AvailabilitySection.js'
import ProductImageGallery from './ProductImageGallery.js'

interface Props {
  organizationId: string
  categories: Category[]
  initial?: Product | null
  disabled: boolean
  pricePrecisionMode?: string
  onSaved: (product: Product, isEdit: boolean) => void
  onCancel: () => void
  hideHeading?: boolean
  onVariantsChanged?: (productId: string, variants: ProductVariant[]) => void
}

export default function ProductForm({
  organizationId,
  categories,
  initial,
  disabled,
  pricePrecisionMode = 'INTEGER_ONLY',
  onSaved,
  onCancel,
  onVariantsChanged,
  hideHeading = false,
}: Props) {
  const { t } = useTranslation()
  const isEdit = initial !== undefined && initial !== null

  const [name, setName] = useState(initial?.name ?? '')
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? '')
  const [price, setPrice] = useState(initial?.baseVariant ? String(initial.baseVariant.price) : '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [composition, setComposition] = useState(initial?.composition ?? '')
  const [weight, setWeight] = useState(initial?.weight != null ? String(initial.weight) : '')
  const [calories, setCalories] = useState(initial?.calories != null ? String(initial.calories) : '')
  const [protein, setProtein] = useState(initial?.protein != null ? String(initial.protein) : '')
  const [fat, setFat] = useState(initial?.fat != null ? String(initial.fat) : '')
  const [carbohydrates, setCarbohydrates] = useState(
    initial?.carbohydrates != null ? String(initial.carbohydrates) : '',
  )
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? '')
  const [sortOrder, setSortOrder] = useState(initial ? String(initial.sortOrder) : '0')
  const [spiceLevel, setSpiceLevel] = useState(initial?.spiceLevel ?? 0)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)
  const [variants, setVariants] = useState<ProductVariant[]>(initial?.variants ?? [])

  const hasCategories = categories.length > 0
  const knownCategory = categories.some(c => c.id === categoryId)
  const idSuffix = isEdit && initial ? initial.id : 'new'
  const priceStep = pricePrecisionMode === 'HALF_UNIT' ? '0.5' : '1'

  function clearErrors() {
    if (Object.keys(errors).length > 0) setErrors({})
  }

  function handleVariantsChanged(productId: string, next: ProductVariant[]) {
    setVariants(next)
    const def = next.find(v => v.isDefault)
    if (def) setPrice(String(def.price))
    onVariantsChanged?.(productId, next)
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    if (validateNameField(name) === 'required') fieldErrors.name = t('error.fieldRequired')
    if (!categoryId) fieldErrors.categoryId = t('products.categoryRequired')
    const priceError = validatePriceField(price)
    if (priceError === 'required') fieldErrors.price = t('products.priceRequired')
    else if (priceError === 'invalid') fieldErrors.price = t('products.priceInvalid')
    if (validateSortOrderField(sortOrder)) fieldErrors.sortOrder = t('products.sortOrderInvalid')
    for (const [key, value, integer] of [
      ['weight', weight, false],
      ['calories', calories, true],
      ['protein', protein, false],
      ['fat', fat, false],
      ['carbohydrates', carbohydrates, false],
    ] as const) {
      if (validateOptionalNumber(value, integer) === 'invalid') {
        fieldErrors[key] = t('products.nutritionInvalid')
      }
    }
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const priceValue = normalizePrice(price)
    if (priceValue === undefined) return

    const weightValue = normalizeOptionalNumber(weight)
    const caloriesValue = normalizeOptionalNumber(calories, true)
    const proteinValue = normalizeOptionalNumber(protein)
    const fatValue = normalizeOptionalNumber(fat)
    const carbohydratesValue = normalizeOptionalNumber(carbohydrates)

    const base = {
      categoryId,
      name: name.trim(),
      price: priceValue,
      sortOrder: normalizeSortOrder(sortOrder) ?? 0,
      spiceLevel,
    }

    setSaving(true)
    setErrors({})
    try {
      const product = initial
        ? await updateProduct(organizationId, initial.id, {
            ...base,
            description: description.trim() || null,
            composition: composition.trim() || null,
            weight: weightValue ?? null,
            calories: caloriesValue ?? null,
            protein: proteinValue ?? null,
            fat: fatValue ?? null,
            carbohydrates: carbohydratesValue ?? null,
            imageUrl: imageUrl.trim() || null,
          })
        : await createProduct(organizationId, {
            ...base,
            description: description.trim() || undefined,
            composition: composition.trim() || undefined,
            weight: weightValue,
            calories: caloriesValue,
            protein: proteinValue,
            fat: fatValue,
            carbohydrates: carbohydratesValue,
            imageUrl: imageUrl.trim() || undefined,
          })
      onSaved(product, isEdit)
    } catch (err: unknown) {
      setErrors(
        toFieldErrors(
          err,
          [
            'name',
            'categoryId',
            'price',
            'sortOrder',
            'description',
            'composition',
            'weight',
            'calories',
            'protein',
            'fat',
            'carbohydrates',
            'imageUrl',
          ],
          t('error.serverError'),
        ),
      )
      setSaving(false)
    }
  }

  return (
    <div className={hideHeading ? 'product-form-embedded' : 'settings-card create-card'}>
      {!hideHeading && <h3>{isEdit ? t('products.editTitle') : t('products.createTitle')}</h3>}
      {!isEdit && <p className="page-section-description">{t('products.createSubtitle')}</p>}

      {!hasCategories && <div className="form-error-banner">{t('products.noCategories')}</div>}
      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`productName-${idSuffix}`}>{t('products.name')} *</label>
            <input
              id={`productName-${idSuffix}`}
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors()
              }}
              maxLength={200}
              required
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`productCategory-${idSuffix}`}>{t('products.category')} *</label>
            <select
              id={`productCategory-${idSuffix}`}
              value={categoryId}
              onChange={e => {
                setCategoryId(e.target.value)
                clearErrors()
              }}
              required
              disabled={saving || disabled || !hasCategories}
            >
              <option value="">{t('products.categoryPlaceholder')}</option>
              {!knownCategory && categoryId && (
                <option value={categoryId}>{t('products.unknownCategory')}</option>
              )}
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.categoryId && <span className="field-error">{errors.categoryId}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`productPrice-${idSuffix}`}>{t('products.price')} *</label>
            <input
              id={`productPrice-${idSuffix}`}
              type="number"
              min={0}
              step={priceStep}
              value={price}
              onChange={e => {
                setPrice(e.target.value)
                clearErrors()
              }}
              required
              disabled={saving || disabled}
            />
            {errors.price && <span className="field-error">{errors.price}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`productSortOrder-${idSuffix}`}>{t('products.sortOrder')}</label>
            <input
              id={`productSortOrder-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
          </div>
        </div>

        <div className="form-field">
          <label htmlFor={`productDescription-${idSuffix}`}>{t('products.description')}</label>
          <textarea
            id={`productDescription-${idSuffix}`}
            value={description}
            onChange={e => {
              setDescription(e.target.value)
              clearErrors()
            }}
            rows={2}
            maxLength={5000}
            disabled={saving || disabled}
          />
        </div>

        <div className="form-field">
          <label htmlFor={`productComposition-${idSuffix}`}>{t('products.composition')}</label>
          <textarea
            id={`productComposition-${idSuffix}`}
            value={composition}
            onChange={e => {
              setComposition(e.target.value)
              clearErrors()
            }}
            rows={2}
            maxLength={5000}
            disabled={saving || disabled}
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`productWeight-${idSuffix}`}>{t('products.weight')}</label>
            <input
              id={`productWeight-${idSuffix}`}
              type="number"
              min={0}
              step="0.1"
              value={weight}
              onChange={e => {
                setWeight(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.weight && <span className="field-error">{errors.weight}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`productCalories-${idSuffix}`}>{t('products.calories')}</label>
            <input
              id={`productCalories-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={calories}
              onChange={e => {
                setCalories(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.calories && <span className="field-error">{errors.calories}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`productProtein-${idSuffix}`}>{t('products.protein')}</label>
            <input
              id={`productProtein-${idSuffix}`}
              type="number"
              min={0}
              step="0.1"
              value={protein}
              onChange={e => {
                setProtein(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.protein && <span className="field-error">{errors.protein}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`productFat-${idSuffix}`}>{t('products.fat')}</label>
            <input
              id={`productFat-${idSuffix}`}
              type="number"
              min={0}
              step="0.1"
              value={fat}
              onChange={e => {
                setFat(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.fat && <span className="field-error">{errors.fat}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`productCarbohydrates-${idSuffix}`}>{t('products.carbohydrates')}</label>
            <input
              id={`productCarbohydrates-${idSuffix}`}
              type="number"
              min={0}
              step="0.1"
              value={carbohydrates}
              onChange={e => {
                setCarbohydrates(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.carbohydrates && <span className="field-error">{errors.carbohydrates}</span>}
          </div>
        </div>

        <div className="form-field">
          <label htmlFor={`productImageUrl-${idSuffix}`}>{t('products.imageUrl')}</label>
          <input
            id={`productImageUrl-${idSuffix}`}
            type="url"
            value={imageUrl}
            onChange={e => {
              setImageUrl(e.target.value)
              clearErrors()
            }}
            maxLength={2000}
            disabled={saving || disabled}
            autoComplete="off"
          />
          <span className="field-hint">{t('products.imageUrlHint')}</span>
          {errors.imageUrl && <span className="field-error">{errors.imageUrl}</span>}
        </div>

        <div className="form-field">
          <label>{t('products.spiceLevel')}</label>
          <div className="spice-selector">
            {[0, 1, 2, 3, 4].map(level => (
              <label key={level} className="spice-option">
                <input
                  type="radio"
                  name={`spice-${idSuffix}`}
                  value={level}
                  checked={spiceLevel === level}
                  onChange={() => setSpiceLevel(level)}
                  disabled={saving || disabled}
                />
                <span>{'🌶'.repeat(level) || t('products.spiceNone')}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || disabled || !hasCategories}>
            {saving ? t('products.saving') : isEdit ? t('products.save') : t('products.create')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('products.cancel')}
          </button>
        </div>
      </form>

      <VariantsEditor
        organizationId={organizationId}
        productId={initial?.id ?? null}
        variants={variants}
        disabled={disabled}
        pricePrecisionMode={pricePrecisionMode}
        onVariantsChange={handleVariantsChanged}
      />

      <ModifierGroupsEditor
        organizationId={organizationId}
        productId={initial?.id ?? null}
        disabled={disabled}
        pricePrecisionMode={pricePrecisionMode}
      />

      <AvailabilitySection
        organizationId={organizationId}
        product={initial ?? null}
        disabled={disabled}
      />

      <ProductImageGallery
        organizationId={organizationId}
        productId={initial?.id ?? null}
        disabled={disabled}
      />
    </div>
  )
}
